import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { doc_folders_endpoint } from "@/config/endpoints";
import { toast } from "sonner";

export type AttachmentFolderDTO = {
  id: number;
  name: string;
  parentId: number | null;
  attachedTo: string;
  attachedScope: number;
  createdAt: string;
  _count?: { documents: number; children: number };
};

export function attachmentFoldersQueryKey(
  attachedTo: string,
  attachedScope: string | number
) {
  return ["attachment-folders", attachedTo, String(attachedScope)] as const;
}

export function useAttachmentFolders(
  attachedTo: string | undefined,
  attachedScope: string | number | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: attachmentFoldersQueryKey(
      attachedTo ?? "",
      attachedScope ?? ""
    ),
    queryFn: async () => {
      const { data } = await axiosInstance.get<{
        success: boolean;
        data: AttachmentFolderDTO[];
      }>(doc_folders_endpoint, {
        params: { attachedTo, attachedScope },
      });
      return data.data ?? [];
    },
    enabled: Boolean(enabled && attachedTo && attachedScope !== ""),
  });
}

export function useAttachmentFolderMutations(
  attachedTo: string,
  attachedScope: string | number
) {
  const queryClient = useQueryClient();
  const qk = attachmentFoldersQueryKey(attachedTo, attachedScope);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: qk });
  };

  const createFolder = useMutation({
    mutationFn: async (payload: {
      name: string;
      parentId?: number | null;
    }) => {
      const { data } = await axiosInstance.post(doc_folders_endpoint, {
        name: payload.name,
        attachedTo,
        attachedScope: Number(attachedScope),
        parentId:
          payload.parentId === undefined || payload.parentId === null
            ? null
            : Number(payload.parentId),
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Folder created");
      invalidate();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Could not create folder"
      );
    },
  });

  const deleteFolder = useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(`${doc_folders_endpoint}/${id}`);
    },
    onSuccess: () => {
      toast.success("Folder removed");
      invalidate();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Could not delete folder"
      );
    },
  });

  const renameFolder = useMutation({
    mutationFn: async (payload: { id: number; name: string }) => {
      const { data } = await axiosInstance.patch(
        `${doc_folders_endpoint}/${payload.id}`,
        {
          name: payload.name,
          attachedTo,
          attachedScope: Number(attachedScope),
        }
      );
      return data;
    },
    onSuccess: () => {
      toast.success("Folder renamed");
      invalidate();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Could not rename folder"
      );
    },
  });

  return { createFolder, deleteFolder, renameFolder, invalidate };
}
